import { useEffect, useRef } from 'react'
import Router from 'next/router'

// Modals are Radix dialogs; an open one renders content with role="dialog"
// and data-state="open". Detect that to drive the page scroll lock.
const MODAL_CONTAINER_SELECTOR = '[role="dialog"][data-state="open"]'

type StyleSnapshot = {
  htmlOverflow: string
  htmlOverscrollBehavior: string
  bodyPosition: string
  bodyTop: string
  bodyLeft: string
  bodyRight: string
  bodyWidth: string
  bodyOverflow: string
  bodyOverscrollBehavior: string
}

const isBodyScrollPinned = (body: HTMLElement): boolean => body.style.position === 'fixed'

const ModalScrollLockManager = () => {
  const isLockedRef = useRef(false)
  const scrollYRef = useRef(0)
  const previousStylesRef = useRef<StyleSnapshot | null>(null)

  useEffect(() => {
    const lockPageScroll = () => {
      const body = document.body

      if (isLockedRef.current) {
        return
      }

      if (isBodyScrollPinned(body)) {
        isLockedRef.current = true
        return
      }

      const html = document.documentElement

      previousStylesRef.current = {
        htmlOverflow: html.style.overflow,
        htmlOverscrollBehavior: html.style.overscrollBehavior,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyRight: body.style.right,
        bodyWidth: body.style.width,
        bodyOverflow: body.style.overflow,
        bodyOverscrollBehavior: body.style.overscrollBehavior,
      }

      scrollYRef.current = window.scrollY

      html.style.overflow = 'hidden'
      html.style.overscrollBehavior = 'none'

      body.style.position = 'fixed'
      body.style.top = `-${scrollYRef.current}px`
      body.style.left = '0'
      body.style.right = '0'
      body.style.width = '100%'
      body.style.overflow = 'hidden'
      body.style.overscrollBehavior = 'none'

      isLockedRef.current = true
    }

    const unlockPageScroll = () => {
      const html = document.documentElement
      const body = document.body
      const shouldUnlock = isLockedRef.current || isBodyScrollPinned(body)

      if (!shouldUnlock) {
        return
      }

      const previousStyles = previousStylesRef.current

      if (previousStyles) {
        html.style.overflow = previousStyles.htmlOverflow
        html.style.overscrollBehavior = previousStyles.htmlOverscrollBehavior
        body.style.position = previousStyles.bodyPosition
        body.style.top = previousStyles.bodyTop
        body.style.left = previousStyles.bodyLeft
        body.style.right = previousStyles.bodyRight
        body.style.width = previousStyles.bodyWidth
        body.style.overflow = previousStyles.bodyOverflow
        body.style.overscrollBehavior = previousStyles.bodyOverscrollBehavior
      } else {
        html.style.overflow = ''
        html.style.overscrollBehavior = ''
        body.style.position = ''
        body.style.top = ''
        body.style.left = ''
        body.style.right = ''
        body.style.width = ''
        body.style.overflow = ''
        body.style.overscrollBehavior = ''
      }

      window.scrollTo(0, scrollYRef.current)

      previousStylesRef.current = null
      isLockedRef.current = false
    }

    const syncLockState = () => {
      const hasOpenModal = document.querySelector(MODAL_CONTAINER_SELECTOR) !== null
      if (hasOpenModal) {
        lockPageScroll()
      } else {
        unlockPageScroll()
      }
    }

    const scheduleSyncLockState = () => {
      syncLockState()
      requestAnimationFrame(syncLockState)
    }

    syncLockState()

    const observer = new MutationObserver(scheduleSyncLockState)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    Router.events.on('routeChangeComplete', syncLockState)

    return () => {
      observer.disconnect()
      Router.events.off('routeChangeComplete', syncLockState)
      unlockPageScroll()
    }
  }, [])

  return null
}

export default ModalScrollLockManager
